import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BlogPost, Challenge, Idea, Maybe, NotificationType, User } from '../../../common/core-api';
import { useFile, useMainModal } from '../../../hooks';
import { CommentNotificationTypes } from '../../../common/enums';
import { removeTags } from '../../../helpers';
import * as Styled from './Header.styled';
import { BlogPostLink, ChallengeLink, IdeaLink } from '.';

export const CommentCardHeader = ({
  type,
  notificationCount,
  commentCardType,
  challenge,
  idea,
  blogPost,
  author,
  isValid
}: {
  type: NotificationType;
  notificationCount: number;
  commentCardType?: CommentNotificationTypes;
  challenge?: Maybe<Challenge> | null;
  idea?: Maybe<Idea>;
  blogPost?: Maybe<BlogPost>;
  author?: Maybe<User>;
  isValid?: boolean;
}) => {
  const intl = useIntl();
  const [openModal] = useMainModal();

  const [getFile] = useFile();
  const url = author?.profileImageFile && getFile({ file: author.profileImageFile, width: 30, height: 30 });
  const challengeTitle = removeTags(challenge?.title || '');
  const challengeLink = (msg: string, id: string | undefined) => (isValid ? ChallengeLink(msg, id) : msg);

  return (
    <>
      {commentCardType &&
        [
          CommentNotificationTypes.REASON_BLOG_SINGLE,
          CommentNotificationTypes.REASON_IDEA_SINGLE,
          CommentNotificationTypes.REASON_IDEA_SAME_USER,
          CommentNotificationTypes.REASON_USER_SINGLE,
          CommentNotificationTypes.REASON_USER_SAME_USER,
          CommentNotificationTypes.REASON_USER_DIFFERENT_CHALLENGES,
          CommentNotificationTypes.REASON_BLOG_SAME_USER,
          CommentNotificationTypes.REASON_TAG
        ].includes(commentCardType) && (
          <Styled.AlignItemsTop>
            {url && <Styled.ProfileImage data-testid="UserProfileImage" src={url} />}
            <Styled.AlignItemsWrap>
              <Styled.LinkedName
                href={`/user/activity/${author?.id}`}
                onClick={(e) =>
                  openModal(e, {
                    title: intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' }),
                    url: `/user/view/${author?.id}`
                  })
                }
              >
                {author?.displayName}
              </Styled.LinkedName>
              {commentCardType === CommentNotificationTypes.REASON_IDEA_SINGLE && (
                <FormattedMessage
                  id="hasCommentedOnAnIdea"
                  defaultMessage="has commented on an idea from the challenge <a>{challengeTitle}</a>"
                  values={{
                    challengeTitle,
                    a: (msg: string) => challengeLink(msg, challenge?.id)
                  }}
                />
              )}
              {(commentCardType === CommentNotificationTypes.REASON_BLOG_SINGLE ||
                commentCardType === CommentNotificationTypes.REASON_BLOG_SAME_USER) && (
                <FormattedMessage
                  id="commentOnBlogPost"
                  defaultMessage="has commented on the blog {itemCount, plural, one {post} other {posts}}"
                  values={{
                    itemCount: notificationCount
                  }}
                />
              )}
              {commentCardType === CommentNotificationTypes.REASON_IDEA_SAME_USER && (
                <FormattedMessage id="hasCommentedOnIdeas" defaultMessage="has commented on these ideas" />
              )}
              {commentCardType === CommentNotificationTypes.REASON_USER_DIFFERENT_CHALLENGES && (
                <FormattedMessage
                  id="hasCommentedOnChallenges"
                  defaultMessage="has commented on challenges"
                  values={{
                    itemCount: notificationCount
                  }}
                />
              )}
              {(commentCardType === CommentNotificationTypes.REASON_USER_SINGLE ||
                commentCardType === CommentNotificationTypes.REASON_USER_SAME_USER) && (
                <FormattedMessage id="hasCommentedOnAChallenge" defaultMessage="has commented on a challenge" />
              )}
              {commentCardType === CommentNotificationTypes.REASON_TAG && (
                <FormattedMessage id="taggedOnComment" defaultMessage="has tagged you on a comment" />
              )}
            </Styled.AlignItemsWrap>
          </Styled.AlignItemsTop>
        )}
      <Styled.AlignItemsWrap>
        {commentCardType === CommentNotificationTypes.REASON_IDEA_SAME_IDEA && (
          <FormattedMessage
            id="ideaHasComments"
            defaultMessage="The idea <a>{ideaTitle}</a> from the challenge <b>{challengeTitle}</b> has new comments"
            values={{
              ideaTitle: removeTags(idea?.title || ''),
              challengeTitle,
              a: (msg: string) => (isValid ? IdeaLink(msg, idea?.id) : msg),
              b: (msg: string) => challengeLink(msg, challenge?.id)
            }}
          />
        )}
        {(commentCardType === CommentNotificationTypes.REASON_USER_SAME_CHALLENGE ||
          commentCardType === CommentNotificationTypes.REASON_CHALLENGE_SINGLE ||
          commentCardType === CommentNotificationTypes.REASON_CHALLENGE_SAME_CHALLENGE) && (
          <FormattedMessage
            id="challengeHasComments"
            defaultMessage="The challenge <a>{challengeTitle}</a> has {itemCount, plural, one {a new comment} other {new comments}}"
            values={{
              challengeTitle,
              a: (msg: string) => challengeLink(msg, challenge?.id),
              itemCount: notificationCount
            }}
          />
        )}
        {commentCardType === CommentNotificationTypes.REASON_BLOG_DIFFERENT_USER && (
          <FormattedMessage
            id="blogPostHasComments"
            defaultMessage="The blog post <a>{blogTitle}</a> has new comments"
            values={{
              blogTitle: blogPost?.title,
              a: (msg: string) => BlogPostLink(msg, blogPost?.id)
            }}
          />
        )}
      </Styled.AlignItemsWrap>
    </>
  );
};
